import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import { Store } from 'store';
import isPopupActive from 'store/app/selector/isPopupActive';
import hidePopupOperation from 'store/app/operation/hidePopupOperation';
import getIsMobile from 'store/app/selector/isMobile';
import { AvailablePopups } from 'store/app/types';
import getHashFromPath from 'utils/getHashFromPath';
import cleanupLocationHash from 'utils/cleanupLocationHash';
import styles from './Popup.module.scss';
import OpIcon from '../op-ui-wrappers/OpIcon/OpIcon';

interface Props {
  popupId: AvailablePopups;
  title?: string | JSX.Element;
  applyButtonTitle?: string;
  children: React.ReactNode;
  wrapperClassName?: string;
  contentClassName?: string;
  closeIconInContent?: boolean;
  onClosePopup?: () => void;
  onShowPopup?: () => void;
  onApplyClick?: () => void;
}

const Popup = ({
  popupId,
  title,
  applyButtonTitle,
  children,
  onClosePopup,
  onShowPopup,
  onApplyClick,
  wrapperClassName,
  contentClassName,
  closeIconInContent,
}: Props) => {
  const isActive = useSelector((store: Store) => isPopupActive(store, popupId));
  const isMobile = useSelector(getIsMobile);
  const dispatch = useDispatch();
  const popupLocationHash = `#${popupId}`;

  if (isActive) {
    document.documentElement.getElementsByTagName('body')[0].style.overflow = 'hidden';
  }

  const applyClick = () => {
    if (onApplyClick) onApplyClick();
    dispatch(hidePopupOperation());
  };

  const closeClick = () => {
    dispatch(hidePopupOperation());
    onClosePopup && onClosePopup();
  };

  const onHashChange = useCallback(
    (e: HashChangeEvent) => {
      if (getHashFromPath(e.oldURL) === popupLocationHash && getHashFromPath(e.newURL) === '') {
        closeClick();
      }
    },
    [closeClick],
  );

  const setupHashChangeHandler = () => {
    window.location.hash = popupLocationHash;
    window.addEventListener('hashchange', onHashChange, false);
  };

  const removeHashChangeHandler = () => {
    window.removeEventListener('hashchange', onHashChange);
    cleanupLocationHash(popupLocationHash);
  };

  useEffect(() => {
    if (isActive) {
      isMobile && setupHashChangeHandler();
      onShowPopup && onShowPopup();
      return () => {
        isMobile && removeHashChangeHandler();
      };
    }
    return () => {};
  }, [isActive]);

  if (!isActive) return null;

  return (
    <div
      className={cn(styles.popup, {
        [styles.mobile]: isMobile,
        [`${wrapperClassName}`]: wrapperClassName,
      })}
    >
      {title && (
        <div
          className={cn(styles.header, {
            [styles.desktop]: !isMobile,
          })}
        >
          {title}
          {!isMobile && (
            <div onClick={closeClick} className={styles.x}>
              <OpIcon icon="close" size="medium" />
            </div>
          )}
        </div>
      )}
      <div className={cn(styles.popupContent, { [`${contentClassName}`]: contentClassName })}>
        {closeIconInContent && (
          <div onClick={closeClick} className={cn(styles.iconInContent, styles.x)}>
            <OpIcon icon="close" size="medium" />
          </div>
        )}
        {children}
      </div>
      {applyButtonTitle && (
        <div className={styles.applyButton} onClick={applyClick}>
          {applyButtonTitle}
        </div>
      )}
    </div>
  );
};

export default Popup;
